import { useState } from 'react';
import { getIntakeRecords, saveIntakeRecord, deleteIntakeRecord, clearIntakeRecords } from '../../../utils/storage/intakeRecords';
import { downloadCSV, parseCSV } from '../../../utils/csv';
import { intakeRecords as intakeRecordsMapper } from '../../../utils/csv/mappers';
import type { IntakeRecord } from '../../../types/data';

export const useIntakeRecords = () => {
  const [records, setRecords] = useState<IntakeRecord[]>(getIntakeRecords());
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<IntakeRecord | undefined>();

  const handleSubmit = (record: IntakeRecord) => {
    saveIntakeRecord(record);
    setRecords(getIntakeRecords());
    setShowForm(false);
    setEditingRecord(undefined);
  };

  const handleEdit = (record: IntakeRecord) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this intake record?')) {
      deleteIntakeRecord(id);
      setRecords(getIntakeRecords());
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRecord(undefined);
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all intake records? This action cannot be undone.')) {
      clearIntakeRecords();
      setRecords([]);
    }
  };

  const handleExport = () => {
    const columns = intakeRecordsMapper.intakeRecordHeaders.map(header => ({
      header,
      accessor: (item: IntakeRecord) => intakeRecordsMapper.toCSV(item)[header]
    }));
    
    downloadCSV(records, columns, 'intake-records');
  };

  const handleImport = async (csvData: string) => {
    try {
      const parsed = await parseCSV(csvData);
      const imported = parsed.map(row => intakeRecordsMapper.fromCSV(row));
      
      // Validate imported data
      const validImports = imported.filter(item => {
        const isValid = item.name && typeof item.name === 'string';
        if (!isValid) {
          console.warn('Skipping invalid import item:', item);
        }
        return isValid;
      });

      if (validImports.length === 0) {
        throw new Error('No valid intake records found in the imported file');
      }

      validImports.forEach(item => saveIntakeRecord(item as IntakeRecord));
      setRecords(getIntakeRecords());
    } catch (error) {
      console.error('Failed to import intake records:', error);
      alert('Failed to import CSV file. Please check the file format and try again.');
    }
  };

  return {
    records,
    showForm,
    editingRecord,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleCancel,
    handleClear,
    handleExport,
    handleImport,
    setShowForm
  };
};